import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import GraficosBasicos from "../../components/solicitacoes/Dashboard/GraficosBasicos";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import AjusteGtcService from "../../services/resource/ajusteGtcService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { kpisBasicos } from "../../utils/solicitacoes/chartOptions";

const service = new AjusteGtcService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));

const FILTROS_INICIAIS_EXTRA = {
    clienteNome: "", clienteCodigo: "", status: null, solicitanteId: null,
};

const DIA_VISITA_LABELS = {
    SEGUNDA_FEIRA: "Segunda-feira",
    TERCA_FEIRA: "Terça-feira",
    QUARTA_FEIRA: "Quarta-feira",
    QUINTA_FEIRA: "Quinta-feira",
    SEXTA_FEIRA: "Sexta-feira",
    SABADO: "Sábado",
};

const simNao = v => v == null ? "-" : (v ? "Sim" : "Não");

const COLUNAS = [
    { key: "clienteNome", label: "Cliente" },
    { key: "clienteCodigo", label: "Código Cliente" },
    { key: "razaoSocial", label: "Razão Social" },
    { key: "fantasia", label: "Nome Fantasia" },
    { key: "tiposAjuste", label: "Tipo de Ajuste" },
    { key: "incluiAlteracaoGtc", label: "Alteração de GTC", render: simNao, center: true },
    { key: "subCanalAtualNome", label: "Subcanal Atual" },
    { key: "subCanalPropostoNome", label: "Subcanal Proposto" },
    { key: "incluiAlteracaoRota", label: "Alteração de Rota", render: simNao, center: true },
    { key: "rotaAtual", label: "Rota Atual" },
    { key: "rotaProposta", label: "Rota Proposta" },
    { key: "incluiAlteracaoVisita", label: "Alteração de Visita", render: simNao, center: true },
    { key: "diaVisitaAtual", label: "Dia de Visita Atual", render: v => DIA_VISITA_LABELS[v] || v },
    { key: "diaVisitaProposto", label: "Dia de Visita Proposto", render: v => DIA_VISITA_LABELS[v] || v },
    { key: "incluiAtualizacaoCoordenadas", label: "Atualizar Coordenadas", render: simNao, center: true },
    { key: "coordenadas", label: "Coordenadas" },
    { key: "justificativaSupervisor", label: "Justificativa (Supervisor)" },
    { key: "justificativaComercial", label: "Justificativa (Comercial)" },
    { key: "confirmacaoAlteracao", label: "Alteração Realizada", render: simNao, center: true },
    { key: "detalhes", label: "Detalhes" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data", format: "date" },
];

export default function RelatorioAjusteGtc() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.ajusteGtc.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Ajuste de GTC / Visita / Rota";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    return (
        <DashboardProcessoLayout
            titulo="Ajuste de GTC / Visita / Rota (Sub-Canal)"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={kpisBasicos(dashboard, "fa fa-route")}
            graficos={<GraficosBasicos dashboard={dashboard} />}
            filtrosExtras={(
                <>
                    <Campo label="Cliente">
                        <input type="text" value={filtros.clienteNome} onChange={e => setFiltro("clienteNome", e.target.value)}
                            placeholder="Razão social ou fantasia" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Código Cliente">
                        <input type="text" value={filtros.clienteCodigo} onChange={e => setFiltro("clienteCodigo", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="dashboard_ajuste_gtc"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
